import { motion, useReducedMotion } from "motion/react";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/**
 * Circular progress ring. Animates the stroke on mount (draws clockwise
 * from 12 o'clock). GPU-friendly — only `stroke-dashoffset` transitions.
 */
export function ProgressRing({
  value,
  size = 116,
  stroke = 12,
  label,
  sublabel,
  color = "var(--color-primary)",
}: {
  value: number;
  size?: number;
  stroke?: number;
  label?: string;
  sublabel?: string;
  color?: string;
}) {
  const reduce = useReducedMotion();
  const clamped = Math.max(0, Math.min(100, value));
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - clamped / 100);

  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-muted)"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={reduce ? { strokeDashoffset: offset } : { strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.9, ease: EASE_OUT, delay: 0.1 }}
        />
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center">
        {label && <span className="font-display text-2xl font-semibold leading-none">{label}</span>}
        {sublabel && <span className="text-muted-foreground mt-1 text-[0.7rem]">{sublabel}</span>}
      </div>
    </div>
  );
}
